<template>
    <v-card
        class="form-card"
        flat
    >
        <v-card-text class="pa-6">
            <v-form
                ref="form"
                autocomplete="off"
                v-model="valid"
                @submit.prevent="submitForm"
            >
                <div class="form-section">
                    <h3 class="form-section-title mb-4">Personal Information</h3>

                    <v-row :dense="$vuetify.breakpoint.width < 600">
                        <v-col
                            cols="12"
                            sm="6"
                        >
                            <v-text-field
                                v-model="form_data.first_name"
                                label="First Name"
                                :rules="[rules.required]"
                                required
                                outlined
                                prepend-inner-icon="mdi-account"
                                autocomplete="off"
                                validate-on-blur
                            />
                        </v-col>

                        <v-col
                            cols="12"
                            sm="6"
                        >
                            <v-text-field
                                v-model="form_data.last_name"
                                label="Last Name"
                                :rules="[rules.required]"
                                required
                                outlined
                                prepend-inner-icon="mdi-account"
                                autocomplete="off"
                                validate-on-blur
                            />
                        </v-col>
                    </v-row>

                    <v-text-field
                        label="Birthday"
                        v-model="form_data.birth_date"
                        placeholder="MM/DD/YYYY"
                        persistent-hint
                        hint="Must be at least 21 years old"
                        @input="onInputBirthday"
                        :rules="[rules.required, rules.required_birthdate, rules.age_verification]"
                        type="tel"
                        outlined
                        prepend-inner-icon="mdi-calendar"
                        class="mb-3"
                        autocomplete="off"
                        validate-on-blur
                    />
                </div>

                <v-divider class="my-6" />

                <div class="form-section">
                    <h3 class="form-section-title mb-4">Contact Information</h3>

                    <slot
                        name="email"
                        :rules="rules"
                    />

                    <slot
                        name="phone"
                        :rules="rules"
                    />
                </div>

                <div
                    v-if="$slots['additional-fields']"
                    class="form-section"
                >
                    <v-divider class="my-6" />
                    <h3 class="form-section-title mb-4">Additional Information</h3>

                    <slot
                        name="additional-fields"
                        :rules="rules"
                    />
                </div>

                <v-btn
                    type="submit"
                    color="primary"
                    block
                    large
                    rounded
                    :disabled="!valid || age_error"
                    :loading="loading"
                    class="mt-8 submit-btn"
                    elevation="2"
                >
                    <v-icon left>mdi-check-circle</v-icon>
                    {{ submit_text }}
                </v-btn>
            </v-form>
        </v-card-text>
    </v-card>
</template>

<script>
export default {
    name: 'GuestCheckInForm',

    props: {
        value: {
            type: Object,
            required: true
        },

        submit_text: {
            type: String,
            default: 'Agree and Submit'
        },

        loading: {
            type: Boolean,
            default: false
        }
    },

    data() {
        return {
            valid: false,
            rules: {
                required: value => !!value || 'This field is required',
                required_birthdate: value => {
                    if (!value) return 'This field is required';
                    // MM/DD/YYYY or MMDDYYYY or MMDDYY
                    const mmddyyyy = /^(0[1-9]|1[0-2])[/]?([0-2][0-9]|3[01])[/]?((19|20)?\d{2})$/;
                    if (!mmddyyyy.test(value)) return 'Enter date as MM/DD/YYYY';
                    return true;
                },
                age_verification: () => !this.age_error || 'You must be at least 21 years old to continue'
            }
        };
    },

    computed: {
        form_data: {
            get() {
                return this.value;
            },
            set(val) {
                this.$emit('input', val);
            }
        },

        age_error() {
            if (!this.form_data.birth_date) return false;
            const today = new Date();
            const birth_date = new Date(this.form_data.birth_date);
            const age = today.getFullYear() - birth_date.getFullYear();
            const month_diff = today.getMonth() - birth_date.getMonth();
            if (month_diff < 0 || (month_diff === 0 && today.getDate() < birth_date.getDate())) {
                return age - 1 < 21;
            }
            return age < 21;
        }
    },

    methods: {
        onInputBirthday(value) {
            const updated = { ...this.form_data, birth_date: this.$rc.maskMMDDYYYY(value) };
            this.form_data = updated;
        },

        submitForm() {
            this.$emit('submit', this.form_data);
        },

        validate() {
            return this.$refs.form.validate();
        }
    }
}
</script>

<style lang="scss" scoped>
.form-card {
    max-width: 100%;
    border-radius: 1rem;
    background: linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%);
}

.form-section {
    margin-bottom: 1rem;

    .form-section-title {
        color: rgba(0, 0, 0, 0.87);
        font-weight: 500;
        font-size: 1.125rem;
        line-height: 1.5;
    }
}

.submit-btn {
    font-weight: 600;
    letter-spacing: 0.05rem;
    text-transform: uppercase;
    transition: all 0.3s ease;

    &:hover:not(:disabled) {
        transform: translateY(-0.125rem);
        box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15) !important;
    }

    &:disabled {
        opacity: 0.6;
    }
}

:deep(.v-text-field--outlined) {
    .v-input__control {
        .v-input__slot {
            border-radius: 0.5rem;
            transition: all 0.2s ease;

            &:hover {
                box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.1);
            }
        }
    }

    &.v-input--is-focused .v-input__slot {
        box-shadow: 0 0.25rem 0.5rem rgba(0, 0, 0, 0.15);
    }
}

:deep(.v-input--dense) {
    .v-input__prepend-inner {
        margin-top: 0.5rem;
        margin-right: 0.75rem;

        .v-icon {
            color: rgba(0, 0, 0, 0.54);
            font-size: 1.25rem;
        }
    }
}

:deep(.v-divider) {
    border-color: rgba(0, 0, 0, 0.08);
    border-width: 0.0625rem;
}

@media (max-width: 599px) {
    .form-card {
        border-radius: 0;
        box-shadow: none !important;
        background: white;
    }

    :deep(.v-card__text) {
        padding: 1rem !important;
    }
}
</style>